import { ReactNode } from 'react';
import { SelfReportType } from '@domain/constant/acca/test/result/selfReportType';
import { VideoType } from '@domain/constant/acca/test/result/videoType';
import { GameType } from '@domain/constant/acca/test/result/gameType';

export enum TUTORIAL_MENU {
  CALIBRATION = '환경체크',
  LIKERT = '성향파악',
  PRACTICE = '전략게임',
  V4 = '영상면접',
}

export type TUTORIAL_MENU_TYPE = keyof typeof TUTORIAL_MENU;

interface TutorialMenu {
  title: TUTORIAL_MENU;
  tabList: TutorialTabMenu[];
}

interface TutorialTabMenu {
  type?: SelfReportType | VideoType | GameType;
  title: string;
  tabTitle?: ReactNode;
  time: string;
  desc: ReactNode;
  url: string;
}

export const tutorialMenuMap: Record<TUTORIAL_MENU_TYPE, TutorialMenu> = {
  CALIBRATION: {
    title: TUTORIAL_MENU.CALIBRATION,
    tabList: [
      {
        title: '환경체크',
        time: '1분',
        desc: (
          <>
            웹 캠과 마이크 환경을 체크할 수 있습니다.
            <br />
            인식이 잘되지 않는 경우, 문제해결 가이드를 확인해 보세요.
          </>
        ),
        url: '/video/acca/tutorial/calibration/videoCalibration1.mp4',
      },
      {
        title: '얼굴등록',
        time: '30초',
        desc: (
          <>
            응시자 본인 확인을 위해 얼굴 등록을 진행합니다.
            <br />
            등록된 얼굴이 마음에 들지 않은 경우 1회 재촬영이 가능합니다.
          </>
        ),
        url: '/video/acca/tutorial/calibration/videoCalibration2.mp4',
      },
    ],
  },
  LIKERT: {
    title: TUTORIAL_MENU.LIKERT,
    tabList: [
      {
        type: 'LIKERT',
        title: '나 알아보기',
        time: '30초',
        desc: (
          <>
            지원자님의 성향을 파악할 수 있는 선택형 질문이 제시됩니다.
            <br />
            주어진 질문을 꼼꼼하게 읽고 자신을 가장 잘 설명하는
            <br />
            선택지를 솔직하게 선택해 보세요.
          </>
        ),
        url: '/video/acca/tutorial/likert/videoLikert1.mp4',
      },
      {
        type: 'META_LIKERT',
        title: '타인 관점에서 나 알아보기',
        tabTitle: (
          <>
            타인 관점에서
            <br />나 알아보기
          </>
        ),
        time: '30초',
        desc: (
          <>
            지원자님의 모습을 다양한 관점에서 생각해 볼 수 있는 질문이 제시됩니다.
            <br />
            제시된 질문과 문항을 잘 읽고 다른 사람의 시선과 나의 시선에서
            <br />
            나를 가장 잘 표현하는 선택지에 솔직하게 응답해 주세요.
          </>
        ),
        url: '/video/acca/tutorial/likert/videoLikert2.mp4',
      },
      {
        type: 'BIPOLAR',
        title: '하나만 선택하기',
        time: '36초',
        desc: (
          <>
            지원자님의 성향이 어느 쪽에 더 가까운지를 알아보기 위해
            <br />
            두 가지 선택지가 주어집니다. 제시된 질문과 선택지를 잘 읽고
            <br />
            자신의 모습과 더 가까운 방향과 정도를 표현해 주세요.
          </>
        ),
        url: '/video/acca/tutorial/likert/videoLikert3.mp4',
      },
      {
        type: 'MULTIPLE_CHOICE',
        title: '여러 개 선택하기',
        time: '1분',
        desc: (
          <>
            지원자님의 성향을 파악할 수 있는 다양한 선택지가 주어집니다.
            <br />
            제시된 질문과 선택지들을 잘 읽고 자신을 가장 잘 표현하는 선택지를
            <br />
            우선순위 혹은 개수에 맞게 선택해 주세요.
          </>
        ),
        url: '/video/acca/tutorial/likert/videoLikert4.mp4',
      },
    ],
  },
  PRACTICE: {
    title: TUTORIAL_MENU.PRACTICE,
    tabList: [
      {
        type: 'RPS',
        title: '가위바위보',
        time: '15초',
        desc: (
          <>
            {`'나' 혹은 '상대'의 입장에서 가위바위보를 하는 과제입니다.`}
            <br />
            {`항상 '나'가 이길 수 있도록, 이기는 혹은 지는 가위바위보를 해 주세요.`}
          </>
        ),
        url: '/video/acca/tutorial/practice/videoPractice1.mp4',
      },
      {
        type: 'MRT',
        title: '도형 회전하기',
        time: '30초',
        desc: (
          <>
            {`주어지는 글자나 도형의 '전'과 '후' 모양을 확인하고 주어진`}
            <br />
            {`회전, 반전 버튼을 활용해서 글자나 도형을 '후'의 모양으로 회전시켜 주세요.`}
          </>
        ),
        url: '/video/acca/tutorial/practice/videoPractice2.mp4',
      },
      {
        type: 'PM',
        title: '약속정하기',
        time: '24초',
        desc: (
          <>
            세 친구가 선호하는 요일, 장소, 메뉴와 각자 타고온 버스 번호가
            <br />
            빠르게 제시됩니다. 이를 잘 기억해 두었다가 주어진 질문에 대한
            <br />
            알맞은 답을 선택해 주세요.
          </>
        ),
        url: '/video/acca/tutorial/practice/videoPractice3.mp4',
      },
      {
        type: 'RMT',
        title: '길 만들기',
        time: '30초',
        desc: (
          <>
            매트릭스 위에 울타리를 설치하여 교통수단을 정해진 손님에게 보내주세요.
            <br />
            정답 울타리 수에 맞는 효율적인 방법을 고민해 보세요.
          </>
        ),
        url: '/video/acca/tutorial/practice/videoPractice4.mp4',
      },
      {
        type: 'PCT2',
        title: '마법약 만들기',
        time: '30초',
        desc: (
          <>
            제시되는 4개의 재료 조합에 따라 빨간약 혹은 파란약이 제조될 수 있습니다.
            <br />
            주어진 조합에 따라 이번에는 어떤 마법약이 제조될 가능성이 높은지 예측해 주세요.
          </>
        ),
        url: '/video/acca/tutorial/practice/videoPractice5.mp4',
      },
      {
        type: 'OTN',
        title: '숫자 누르기',
        time: '30초',
        desc: (
          <>
            신호가 제시되면 주어진 규칙에 맞게 숫자 버튼을
            <br />
            최대한 빠르고 정확하게 눌러 주세요.
          </>
        ),
        url: '/video/acca/tutorial/practice/videoPractice6.mp4',
      },
      {
        type: 'FNB',
        title: '도형 순서 기억하기',
        tabTitle: (
          <>
            도형 순서
            <br />
            기억하기
          </>
        ),
        time: '24초',
        desc: (
          <>
            화면에 순차적으로 다양한 도형이 제시됩니다.
            <br />
            제시되는 도형을 순서대로 기억하면서, 지금 화면에 제시되어 있는 도형이
            <br />
            기억 속 두 번째 전 혹은 세 번째 전의 도형과 같은지 판단해 주세요.
          </>
        ),
        url: '/video/acca/tutorial/practice/videoPractice7.mp4',
      },
      {
        type: 'HAS',
        title: '고양이 술래잡기',
        time: '30초',
        desc: (
          <>
            생쥐 무리가 등장하는 위치를 기억하여 고양이가 생쥐를 찾았는지 판단해 주세요.
            <br />
            자신의 기억을 얼마나 확신하는지도 솔직하게 응답해 주세요.
          </>
        ),
        url: '/video/acca/tutorial/practice/videoPractice8.mp4',
      },
      {
        type: 'WNC',
        title: '개수 비교하기',
        time: '20초',
        desc: (
          <>
            화면 왼쪽과 오른쪽에 단어가 여러 개 제시됩니다.
            <br />두 단어 중 어떤 단어의 개수가 더 많았는지 선택해 주세요.
          </>
        ),
        url: '/video/acca/tutorial/practice/videoPractice9.mp4',
      },
    ],
  },
  V4: {
    title: TUTORIAL_MENU.V4,
    tabList: [
      {
        title: '자기소개',
        time: '2분',
        desc: <>차분하게 자신에 대해 소개해 주세요.</>,
        url: '/video/acca/tutorial/v4/videoV4_1.mp4',
      },
      {
        title: '기본 면접',
        time: '2분',
        desc: (
          <>
            지원 동기와 같이 기본적인 질문이 주어집니다.
            <br />
            주어진 질문에 침착하게 답변해 보세요.
          </>
        ),
        url: '/video/acca/tutorial/v4/videoV4_2.mp4',
      },
      {
        type: 'VIDEO_BASIC_EXPERIENCE',
        title: '경험 기반 면접',
        time: '5분',
        desc: (
          <>
            자신의 경험에 대한 질문이 주어집니다.
            <br />
            나의 성공 경험과 실패 경험을 떠올려보고 이어지는 질문들에 응답해 보세요.
          </>
        ),
        url: '/video/acca/tutorial/v4/videoV4_3.mp4',
      },
      {
        type: 'VIDEO_BASIC_SITUATION',
        title: '상황 면접',
        time: '2분',
        desc: (
          <>
            다양한 상황이 무작위로 주어지게 됩니다.
            <br />
            주어진 상황에서 어떤 선택을 더 선호하는지와 그 이유를 솔직하게 응답해 보세요.
          </>
        ),
        url: '/video/acca/tutorial/v4/videoV4_4.mp4',
      },
      {
        type: 'VIDEO_BASIC_VALUES',
        title: '가치관 면접',
        time: '5분',
        desc: (
          <>
            일과 관계에서 응시자님이 가지고 있는 가치관을 알 수 있는 질문들이 주어집니다.
            <br />
            평소 나의 가치관을 돌아보고 이어지는 질문들에 응답해 보세요.
          </>
        ),
        url: '/video/acca/tutorial/v4/videoV4_5.mp4',
      },
      {
        type: 'VIDEO_BASIC_FREE_CHOICE',
        title: '질문 선택 면접',
        time: '2분',
        desc: (
          <>
            다양한 질문이 주어집니다. 주어진 여러 질문 중 가장 자신 있게
            <br />
            답할 수 있는 질문을 직접 선택하여 응답해 보세요.
          </>
        ),
        url: '/video/acca/tutorial/v4/videoV4_6.mp4',
      },
    ],
  },
};
